import { api } from "@/lib/apiClient";
import type { CurrentUser } from "@/lib/auth";
import { isChristmasSeason, isHalloweenSeason } from "@/lib/season";
import { isDevVacationSimulated } from "@/lib/vacation";

export type DecoPreference = "full" | "subtle" | "off";

export const DECO_STORAGE_KEY = "eh-deco-preference";
export const DECO_CHANGE_EVENT = "eh:deco-change";

const VALID: DecoPreference[] = ["full", "subtle", "off"];

export function isDecoPreference(value: unknown): value is DecoPreference {
  return typeof value === "string" && VALID.includes(value as DecoPreference);
}

export function cacheDecoPreferenceLocal(preference: DecoPreference): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DECO_STORAGE_KEY, preference);
  } catch {
    // ignore (private mode etc.)
  }
}

export function loadDecoPreferenceLocal(): DecoPreference {
  if (typeof window === "undefined") return "full";
  try {
    const stored = window.localStorage.getItem(DECO_STORAGE_KEY);
    if (isDecoPreference(stored)) return stored;
  } catch {
    // ignore
  }
  return "full";
}

export function dispatchDecoPreferenceChange(preference: DecoPreference): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DECO_CHANGE_EVENT, { detail: { preference } }));
}

/** @deprecated Use loadDecoPreferenceLocal – kept for call sites during migration. */
export function loadDecoPreference(): DecoPreference {
  return loadDecoPreferenceLocal();
}

export async function fetchDecoPreferenceFromServer(): Promise<DecoPreference> {
  const data = await api.get<{ preference?: unknown }>("/me/deco-preference");
  const pref = data?.preference;
  if (isDecoPreference(pref)) return pref;
  return "full";
}

export async function persistDecoPreferenceToServer(preference: DecoPreference): Promise<DecoPreference> {
  const data = await api.put<{ preference?: unknown }>("/me/deco-preference", { preference });
  const pref = data?.preference;
  if (isDecoPreference(pref)) return pref;
  return preference;
}

export function applyDecoPreferenceLocally(preference: DecoPreference): void {
  cacheDecoPreferenceLocal(preference);
  dispatchDecoPreferenceChange(preference);
}

export async function saveDecoPreference(next: DecoPreference): Promise<void> {
  applyDecoPreferenceLocally(next);
  try {
    const saved = await persistDecoPreferenceToServer(next);
    applyDecoPreferenceLocally(saved);
  } catch {
    // Lokale Auswahl bleibt aktiv; Server-Sync beim nächsten Laden.
  }
}

/** Dezember / Halloween / Geburtstag (oder Dev-Simulation) – Deko kann bzw. würde laufen. */
export function isDecoSeasonActive(
  user: CurrentUser | null | undefined,
  mineIsBirthday: boolean,
  vacationActive = false,
): boolean {
  return (
    isChristmasSeason(user) ||
    isHalloweenSeason(user) ||
    mineIsBirthday ||
    vacationActive ||
    isDevVacationSimulated(user)
  );
}

export function decoAllowsBirthdayConfetti(preference: DecoPreference): boolean {
  return preference === "full";
}

export function decoAllowsBirthdayAvatarBadge(preference: DecoPreference): boolean {
  return preference === "full";
}

export function decoAllowsHomeBirthdayEffects(preference: DecoPreference): boolean {
  return preference === "full";
}

export function decoAllowsHomeBirthdayWidget(preference: DecoPreference): boolean {
  return preference === "full" || preference === "subtle";
}

export function decoAllowsChristmasSnow(preference: DecoPreference): boolean {
  return preference === "full" || preference === "subtle";
}

export function decoAllowsChristmasDecor(preference: DecoPreference): boolean {
  return preference === "full";
}

/** Fledermäuse / Nebel-Layer (wie Schnee: auch bei „Dezent“). */
export function decoAllowsHalloweenBats(preference: DecoPreference): boolean {
  return preference === "full" || preference === "subtle";
}

/** Kürbisse und Spinnennetze (auch bei „Dezent“). */
export function decoAllowsHalloweenDecor(preference: DecoPreference): boolean {
  return preference === "full" || preference === "subtle";
}

/** Girlande, Laternen und Hintergrundtönung (nur „Voll“). */
export function decoAllowsHalloweenFullDecor(preference: DecoPreference): boolean {
  return preference === "full";
}

export function decoAllowsVacationBubbles(preference: DecoPreference): boolean {
  return preference === "full";
}

export function decoAllowsVacationDecor(preference: DecoPreference): boolean {
  return preference === "full" || preference === "subtle";
}
